import { createContext, PropsWithChildren, useContext } from 'react';

interface StatusViewContextValue {
  compact: boolean;
  /** Measured width of one grid item; 0 until first measure. */
  itemWidth: number;
}

const StatusViewContext = createContext<StatusViewContextValue | undefined>(undefined);

interface StatusViewProviderProps {
  compact: boolean;
  /** Measured width of one grid item; 0 until first measure. */
  itemWidth: number;
}

/**
 * Provider component that supplies compact mode state to all child components.
 * Used to avoid prop drilling when components need to know if they're in grid (compact) or list view.
 */
export function StatusViewProvider({
  compact,
  itemWidth,
  children,
}: PropsWithChildren<StatusViewProviderProps>) {
  return (
    <StatusViewContext.Provider value={{ compact, itemWidth }}>
      {children}
    </StatusViewContext.Provider>
  );
}

/**
 * Hook to access the status view context.
 * Throws an error if used outside of StatusViewProvider.
 */
export function useStatusViewContext(): StatusViewContextValue {
  const context = useContext(StatusViewContext);
  if (context === undefined) {
    throw new Error('useStatusViewContext must be used within a StatusViewProvider');
  }
  return context;
}
